import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Center, Icon, Spinner } from '@barev/ui';
import { errorText, getInitData, haptic, useI18n, useToast, useTRPC } from '@barev/web';
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PageHead } from '../lib.tsx';

const MAX_FILE = 10 * 1024 * 1024;

/**
 * Чат с командой Barev Club: та же переписка, что в чате с ботом.
 * Подписчик пишет личному консьержу, остальные — в поддержку; ответ приходит и сюда, и в Telegram.
 */
export function Chat() {
  const { t, dt } = useI18n();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const chat = useQuery({ ...trpc.client.chat.get.queryOptions(), refetchInterval: 5_000 });
  const [text, setText] = useState('');
  const [uploading, setUploading] = useState(false);
  const list = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: trpc.client.chat.get.queryKey() });
  const send = useMutation(
    trpc.client.chat.send.mutationOptions({
      onMutate: () => haptic.tap(),
      onSuccess: () => {
        setText('');
        void refresh();
      },
      onError: (e) => toast(errorText(t, e), 'error'),
    }),
  );
  const setChannel = useMutation(trpc.client.chat.setChannel.mutationOptions({ onSuccess: () => void refresh(), onError: (e) => toast(errorText(t, e), 'error') }));

  const lastId = chat.data?.messages.at(-1)?.id;
  useLayoutEffect(() => {
    list.current?.scrollTo({ top: list.current.scrollHeight });
  }, [lastId]);

  const upload = async (file: File) => {
    if (file.size > MAX_FILE) return toast(t('client.chat.fileTooBig'), 'error');
    setUploading(true);
    try {
      const form = new FormData();
      form.set('text', text);
      form.set('file', file);
      const res = await fetch('/files/client/chat', { method: 'POST', body: form, headers: { authorization: `tma ${getInitData()}` } });
      if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error ?? String(res.status));
      setText('');
      void refresh();
    } catch (e) {
      toast(errorText(t, e), 'error');
    } finally {
      setUploading(false);
    }
  };

  if (!chat.data)
    return (
      <Center>
        <Spinner />
      </Center>
    );
  const d = chat.data;
  let prevDay = '';

  return (
    <main className="chat-screen">
      <div className="chat-screen__head">
        <PageHead title={t('client.chat.title')} />
        {d.canConcierge ? (
          <div className="chat-switch" role="group">
            {(['CONCIERGE', 'SUPPORT'] as const).map((c) => (
              <button key={c} type="button" aria-pressed={d.channel === c} disabled={setChannel.isPending} onClick={() => d.channel !== c && setChannel.mutate({ channel: c })}>
                {t(c === 'CONCIERGE' ? 'client.chat.concierge' : 'client.chat.support')}
              </button>
            ))}
          </div>
        ) : (
          <a href="#/membership" className="chat-upsell bc-small">
            {t('client.chat.conciergeOnly')} →
          </a>
        )}
      </div>

      <div className="chat-list" ref={list}>
        <p className="chat-hint bc-small bc-muted">{t('client.chat.hint')}</p>
        {d.messages.length === 0 && <p className="chat-hint">{t('client.chat.empty')}</p>}
        {d.messages.map((m) => {
          const day = dt(m.createdAt, { day: 'numeric', month: 'long' });
          const sep = day !== prevDay ? <div className="chat-day">{day}</div> : null;
          prevDay = day;
          return (
            <Fragment key={m.id}>
              {sep}
              <div className={`chat-msg ${m.mine ? 'chat-msg--mine' : ''}`}>
                {!m.mine && <div className="chat-msg__who">{t(m.channel === 'CONCIERGE' ? 'client.chat.concierge' : 'client.chat.support')}</div>}
                {m.file && <ChatFile id={m.id} file={m.file} />}
                {m.text && <div className="chat-msg__text">{m.text}</div>}
                <div className="chat-msg__time">{dt(m.createdAt, { hour: '2-digit', minute: '2-digit' })}</div>
              </div>
            </Fragment>
          );
        })}
      </div>

      <form
        className="chat-compose"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim() && !send.isPending) send.mutate({ text });
        }}
      >
        <button type="button" className="chat-compose__btn" onClick={() => fileInput.current?.click()} disabled={uploading} aria-label={t('client.chat.attach')}>
          {uploading ? <Spinner /> : <Icon name="camera" size={22} />}
        </button>
        <input
          ref={fileInput}
          type="file"
          hidden
          accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void upload(f);
          }}
        />
        <textarea className="chat-compose__input" rows={1} value={text} maxLength={4000} placeholder={t('client.chat.placeholder')} onChange={(e) => setText(e.target.value)} aria-label={t('client.chat.placeholder')} />
        <button type="submit" className="chat-compose__send" disabled={!text.trim() || send.isPending} aria-label={t('client.chat.send')}>
          <Icon name="arrow" size={22} strokeWidth={2.4} />
        </button>
      </form>
    </main>
  );
}

/** Файл из переписки: картинку показываем, остальное — ссылкой. Доступ по initData, поэтому через fetch */
function ChatFile({ id, file }: { id: string; file: { name: string | null; mime: string | null } }) {
  const { t } = useI18n();
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let revoke: string | null = null;
    fetch(`/files/client/chat-file/${id}`, { headers: { authorization: `tma ${getInitData()}` } })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => {
        if (!b) return;
        revoke = URL.createObjectURL(b);
        setUrl(revoke);
      })
      .catch(() => {});
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [id]);
  if (file.mime?.startsWith('image/') && url) return <img className="chat-msg__img" src={url} alt={file.name ?? ''} />;
  return (
    <a className="chat-msg__file" href={url ?? undefined} download={file.name ?? undefined} target="_blank" rel="noreferrer">
      📎 {file.name ?? t('client.chat.file')}
    </a>
  );
}
